import { useTranslation } from 'react-i18next'
import { getMediaUrl } from '@/utils/media'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import {
  Award,
  Target,
  Lightbulb,
  Leaf,
  Users,
  Globe,
  Building2,
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  Factory,
  Microscope,
} from 'lucide-react'
import { SEO } from '@/components/SEO'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'

// Animated counter hook
function useCounter(end: number, duration: number = 2000) {
  const [count, setCount] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!hasStarted) return
    let startTime: number
    let animationFrame: number
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)
      setCount(Math.floor(progress * end))
      if (progress < 1) animationFrame = requestAnimationFrame(animate)
    }
    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [end, duration, hasStarted])

  useEffect(() => {
    if (!ref.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return { count, ref }
}

// Value card component
function ValueCard({
  icon: Icon,
  title,
  description,
  color,
  delay,
}: {
  icon: React.ElementType
  title: string
  description: string
  color: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl bg-white shadow-lg border border-slate-200 relative overflow-hidden h-full flex flex-col"
    >
      {/* Top accent */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: color }}
      />
      <div
        className="w-12 h-12 sm:w-[60px] sm:h-[60px] rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: color }} />
      </div>
      <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-slate-500 leading-relaxed">{description}</p>
    </motion.div>
  )
}

// Timeline item component
function TimelineItem({
  year,
  title,
  description,
  index,
  isLast,
}: {
  year: string
  title: string
  description: string
  index: number
  isLast: boolean
}) {
  const isLeft = index % 2 === 0

  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="flex items-start gap-3 sm:gap-6 md:gap-8 relative"
    >
      {/* Year badge */}
      <div className="w-16 sm:w-20 md:w-24 flex-shrink-0 text-right pt-1">
        <span
          className="inline-block px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-full text-white font-bold text-xs sm:text-sm"
          style={{ background: 'linear-gradient(135deg, #00a3cc 0%, #0077b3 100%)' }}
        >
          {year}
        </span>
      </div>

      {/* Line and dot */}
      <div className="flex flex-col items-center">
        <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-accent border-2 sm:border-4 border-sky-100 flex-shrink-0" />
        {!isLast && (
          <div className="w-0.5 flex-1 min-h-16 sm:min-h-20 bg-slate-200" />
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 ${isLast ? '' : 'pb-4 sm:pb-6 md:pb-8'}`}>
        <div className="p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-200">
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2">
            {title}
          </h3>
          <p className="text-sm sm:text-base text-slate-500 leading-relaxed">{description}</p>
        </div>
      </div>
    </motion.div>
  )
}

export function AboutPage() {
  const { t } = useTranslation()
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  })

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200])
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  const stats = [
    { value: 2007, suffix: '', label: t('about.stat1Label') },
    { value: 18, suffix: '+', label: t('about.stat2Label') },
    { value: 55, suffix: '', label: t('about.stat3Label') },
    { value: 100, suffix: '%', label: t('about.stat4Label') },
  ]

  const timeline = [
    { year: '2007', title: t('about.timeline.2007.title'), description: t('about.timeline.2007.description') },
    { year: '2010', title: t('about.timeline.2010.title'), description: t('about.timeline.2010.description') },
    { year: '2011', title: t('about.timeline.2011.title'), description: t('about.timeline.2011.description') },
    { year: '2023', title: t('about.timeline.2023.title'), description: t('about.timeline.2023.description') },
    { year: '2024', title: t('about.timeline.2024.title'), description: t('about.timeline.2024.description') },
  ]

  const values = [
    {
      icon: Award,
      title: t('about.value1Title'),
      description: t('about.value1Text'),
      color: '#003b7a',
    },
    {
      icon: Lightbulb,
      title: t('about.value2Title'),
      description: t('about.value2Text'),
      color: '#f59e0b',
    },
    {
      icon: Leaf,
      title: t('about.value3Title'),
      description: t('about.value3Text'),
      color: '#10b981',
    },
  ]

  return (
    <>
      <SEO
        title={t('about.metaTitle')}
        description={t('about.metaDescription')}
        image={getMediaUrl('about/company-team-facilities.jpg')}
      />

      {/* ============ HERO SECTION ============ */}
      <section
        ref={heroRef}
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          marginTop: '-80px',
          paddingTop: '80px',
        }}
      >
        {/* Background Image with Parallax */}
        <motion.div style={{ position: 'absolute', inset: 0, y: heroY }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${getMediaUrl('about/company-team-facilities.jpg')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: 'scale(1.1)',
            }}
          />
        </motion.div>

        {/* Gradient Overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, rgba(0,31,66,0.95) 0%, rgba(0,59,122,0.85) 50%, rgba(0,31,66,0.9) 100%)`,
          }}
        />

        {/* Grid Pattern */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(rgba(0,163,204,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,163,204,0.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Floating Elements */}
        <motion.div
          animate={{ y: [-10, 10, -10], rotate: [0, 5, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
          style={{
            position: 'absolute',
            top: '15%',
            right: '10%',
            opacity: 0.1,
          }}
        >
          <Building2 style={{ width: '200px', height: '200px', color: '#00a3cc' }} />
        </motion.div>

        {/* Hero Content */}
        <motion.div style={{ opacity: heroOpacity }} className="container-main relative z-10">
          <div style={{ maxWidth: '900px' }}>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '2rem',
                backgroundColor: 'rgba(0,163,204,0.2)',
                border: '1px solid rgba(0,163,204,0.3)',
                marginBottom: '1.5rem',
              }}
            >
              <Users style={{ width: '16px', height: '16px', color: '#00a3cc' }} />
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#00a3cc',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('about.since')}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              style={{
                fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                fontWeight: 800,
                color: '#ffffff',
                lineHeight: 1.1,
                marginBottom: '1.5rem',
              }}
            >
              {t('about.heroTitle')}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                fontSize: '1.25rem',
                color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.6,
                maxWidth: '700px',
                marginBottom: '2.5rem',
              }}
            >
              {t('about.heroSubtitle')}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}
            >
              <LocalizedLink to="/contact">
                <Button variant="primary" size="lg">
                  {t('buttons.contactUs')}
                  <ArrowRight style={{ marginLeft: '0.5rem', width: '20px', height: '20px' }} />
                </Button>
              </LocalizedLink>
              <LocalizedLink to="/certifications">
                <Button
                  variant="outline"
                  size="lg"
                  style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#ffffff' }}
                >
                  {t('about.ourCertifications')}
                </Button>
              </LocalizedLink>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          style={{
            position: 'absolute',
            bottom: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            style={{
              width: '24px',
              height: '40px',
              borderRadius: '12px',
              border: '2px solid rgba(255,255,255,0.3)',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '8px',
            }}
          >
            <motion.div
              style={{
                width: '4px',
                height: '8px',
                borderRadius: '2px',
                backgroundColor: '#00a3cc',
              }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* ============ STATS SECTION ============ */}
      <section
        style={{
          padding: '5rem 0',
          background: 'linear-gradient(135deg, #001f42 0%, #003b7a 100%)',
        }}
      >
        <div className="container-main">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '3rem',
              textAlign: 'center',
            }}
          >
            {stats.map((stat, index) => {
              const { count, ref } = useCounter(stat.value)
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <span
                    ref={ref}
                    style={{
                      fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #00a3cc 0%, #ffffff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.value > 2000 ? count : count.toLocaleString()}
                    {stat.suffix}
                  </span>
                  <p style={{ marginTop: '0.5rem', fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }}>
                    {stat.label}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============ OVERVIEW SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#ffffff' }}>
        <div className="container-main">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
              gap: '4rem',
              alignItems: 'center',
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#00a3cc',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {t('about.ourHistoryLabel')}
              </span>
              <h2
                style={{
                  fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                  fontWeight: 800,
                  color: '#0f172a',
                  marginTop: '0.5rem',
                  marginBottom: '1.5rem',
                }}
              >
                {t('about.overviewTitle')}
              </h2>
              <p style={{ fontSize: '1.125rem', color: '#64748b', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                {t('about.overviewText')}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  t('about.bulletMadeInItaly'),
                  t('about.bulletIsoCertifications'),
                  t('about.bulletAmppMember'),
                  t('about.bulletSpecializedTeam'),
                ].map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <CheckCircle2 style={{ width: '20px', height: '20px', color: '#10b981', flexShrink: 0 }} />
                    <span style={{ color: '#334155', fontWeight: 500 }}>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              style={{ position: 'relative' }}
            >
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getMediaUrl('about/manufacturing-process.jpeg')}
                  alt="Manufacturing process"
                  className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover"
                />
              </div>

              {/* Floating badge - hidden on mobile */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="hidden sm:block absolute -bottom-4 sm:-bottom-5 -left-4 sm:-left-5 p-3 sm:p-6 rounded-lg sm:rounded-xl"
                style={{
                  backgroundColor: '#003b7a',
                  boxShadow: '0 20px 40px rgba(0,59,122,0.3)',
                }}
              >
                <Factory className="w-6 h-6 sm:w-8 sm:h-8 text-accent" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ MISSION SECTION ============ */}
      <section
        style={{
          padding: '6rem 0',
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        }}
      >
        <div className="container-main">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
              gap: '4rem',
              alignItems: 'center',
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              style={{ position: 'relative' }}
            >
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getMediaUrl('about/laboratory-equipment.jpg')}
                  alt="Laboratory"
                  className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover"
                />
              </div>

              {/* Floating badge - hidden on mobile */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="hidden sm:block absolute -bottom-4 sm:-bottom-5 -right-4 sm:-right-5 p-3 sm:p-6 rounded-lg sm:rounded-xl"
                style={{
                  backgroundColor: '#00a3cc',
                  boxShadow: '0 20px 40px rgba(0,163,204,0.3)',
                }}
              >
                <Microscope className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#00a3cc',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {t('about.ourMissionLabel')}
              </span>
              <h2
                style={{
                  fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                  fontWeight: 800,
                  color: '#0f172a',
                  marginTop: '0.5rem',
                  marginBottom: '1.5rem',
                }}
              >
                {t('about.missionTitle')}
              </h2>
              <p style={{ fontSize: '1.125rem', color: '#64748b', lineHeight: 1.7, marginBottom: '2rem' }}>
                {t('about.missionText')}
              </p>

              <div
                style={{
                  padding: '1.5rem',
                  borderRadius: '1rem',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Target style={{ width: '32px', height: '32px', color: '#0284c7' }} />
                  <div>
                    <p style={{ fontWeight: 600, color: '#0f172a' }}>{t('about.ourGoal')}</p>
                    <p style={{ fontSize: '0.95rem', color: '#64748b' }}>
                      {t('about.ourGoalText')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ VALUES SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#ffffff' }}>
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '3rem' }}
          >
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00a3cc',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {t('about.ourPrinciplesLabel')}
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                fontWeight: 800,
                color: '#0f172a',
                marginTop: '0.5rem',
              }}
            >
              {t('about.valuesTitle')}
            </h2>
          </motion.div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1.5rem',
              justifyContent: 'center',
              alignItems: 'stretch',
            }}
          >
            {values.map((value, index) => (
              <div key={index} style={{ flex: '1 1 300px', maxWidth: '380px', display: 'flex' }}>
                <ValueCard {...value} delay={index * 0.1} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TIMELINE SECTION ============ */}
      <section
        style={{
          padding: '6rem 0',
          backgroundColor: '#f8fafc',
        }}
      >
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00a3cc',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {t('about.ourEvolutionLabel')}
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                fontWeight: 800,
                color: '#0f172a',
                marginTop: '0.5rem',
              }}
            >
              {t('about.timelineTitle')}
            </h2>
          </motion.div>

          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            {timeline.map((item, index) => (
              <TimelineItem
                key={item.year}
                year={item.year}
                title={item.title}
                description={item.description}
                index={index}
                isLast={index === timeline.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section
        style={{
          padding: '6rem 0',
          background: 'linear-gradient(135deg, #003b7a 0%, #001f42 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative */}
        <motion.div
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            border: '1px solid rgba(0,163,204,0.1)',
          }}
        />

        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}
          >
            <Globe
              style={{
                width: '48px',
                height: '48px',
                color: '#00a3cc',
                margin: '0 auto 1.5rem',
              }}
            />
            <h2
              style={{
                fontSize: 'clamp(2rem, 5vw, 2.75rem)',
                fontWeight: 800,
                color: '#ffffff',
                marginBottom: '1.5rem',
              }}
            >
              {t('about.readyToCollaborate')}
            </h2>
            <p
              style={{
                fontSize: '1.25rem',
                color: 'rgba(255,255,255,0.7)',
                marginBottom: '2.5rem',
                lineHeight: 1.6,
              }}
            >
              {t('about.readyToCollaborateText')}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
              <LocalizedLink to="/contact">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="primary" size="lg">
                    {t('buttons.contactUs')}
                    <ChevronRight style={{ marginLeft: '0.5rem', width: '20px', height: '20px' }} />
                  </Button>
                </motion.div>
              </LocalizedLink>

              <LocalizedLink to="/cathodic-protection">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    style={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: '#ffffff',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    }}
                  >
                    {t('about.discoverProducts')}
                  </Button>
                </motion.div>
              </LocalizedLink>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
